import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Loader2, Gauge, Upload } from "lucide-react";
import api from "../lib/api";
import { MargenCard } from "../components/MargenCard";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { KpiTile, ParcialAviso } from "../components/indicadores/kpi";
import { Destacados } from "../components/indicadores/Destacados";
import { IndicadoresVentasSeccion, AccesosIndicadores } from "./IndicadoresVentas";
import { SubirArriba } from "../components/SubirArriba";

const DESTACADOS = ["facturacion", "operaciones", "ticket_medio", "venta_libre", "ratio_consejo", "margen_pct"];

const GRAFICOS = [
  { key: "facturacion", label: "Facturación" },
  { key: "operaciones", label: "Operaciones" },
  { key: "ticket_medio", label: "Ticket medio" },
  { key: "peso_venta_libre", label: "% venta libre" },
  { key: "peso_parafarmacia", label: "% parafarmacia" },
  { key: "ratio_consejo", label: "Recetas con consejo" },
  { key: "margen_pct", label: "% margen (ventas con coste conocido)" },
  { key: "ratio_d_muerto", label: "Ratio D muerto" },
];

const shift = (mes, n) => {
  const [y, m] = mes.split("-").map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};

function Evolucion({ meses, mes }) {
  const [metric, setMetric] = useState("facturacion");
  const byMes = useMemo(() => Object.fromEntries(meses.map((m) => [m.mes, m])), [meses]);
  const data = useMemo(() => meses.filter((m) => m.mes <= mes && m.mes > shift(mes, -12)).map((m) => ({
    label: m.label, actual: m[metric] ?? null, anterior: byMes[shift(m.mes, -12)]?.[metric] ?? null,
  })), [meses, mes, metric, byMes]);
  const hayAnterior = data.some((d) => d.anterior != null);
  return (
    <div className="card p-6 mb-6" data-testid="cuadro-evolucion">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Últimos 12 meses</div>
          <h3 className="font-display text-xl font-bold">Evolución</h3>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {GRAFICOS.map((g) => (
            <button key={g.key} onClick={() => setMetric(g.key)} data-testid={`cuadro-grafico-${g.key}`}
                    className={"px-3 py-1 rounded-full text-xs font-semibold border transition-colors " +
                      (metric === g.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400")}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => v.toLocaleString("es-ES", { maximumFractionDigits: 1 })} />
            <Tooltip formatter={(v) => (v == null ? "—" : v.toLocaleString("es-ES", { maximumFractionDigits: 2 }))} />
            <Legend />
            <Bar dataKey="actual" name="Este periodo" fill="#0f172a" radius={[4, 4, 0, 0]} />
            {hayAnterior && <Line dataKey="anterior" name="Mismo mes año anterior" stroke="#10b981" strokeWidth={2} dot connectNulls />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CuadroVentas() {
  const [mes, setMes] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/ventas", { params: mes ? { mes } : {} })
      .then((r) => setData(r.data)).catch(() => setData({ meses: [] })).finally(() => setLoading(false));
  }, [mes]);

  if (!data) return <div className="p-8"><Loader2 className="animate-spin text-slate-400" /></div>;
  const comp = data.comparativa;
  const filas = Object.fromEntries((comp?.filas || []).map((f) => [f.key, f]));
  const actual = data.meses.find((m) => m.mes === comp?.mes);

  return (
    <div className="p-4 sm:p-8 max-w-7xl" data-testid="cuadro-ventas-page">
      <BannerFarmanaliza compact image="/img/farmanaliza-titular.jpg?v=2" kicker="Titular" subtitle="Cuadro de mando de ventas"
                         text="Tus ventas del mes frente al mes anterior y al mismo mes del año pasado." testid="banner-titular" />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1.5"><Gauge size={12} /> Comercial</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Cuadro de mando de ventas</h1>
          {comp && (
            <p className="text-slate-500 mt-2 text-sm" data-testid="cuadro-subtitulo">
              {comp.actual} · comparado con {comp.anterior || "—"}{comp.anio_anterior ? ` y con ${comp.anio_anterior}` : ""}
            </p>
          )}
        </div>
        {data.meses.length > 0 && (
          <div className="flex items-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
            <select className="input text-sm py-1.5" style={{ width: 190 }} value={comp?.mes || ""} onChange={(e) => setMes(e.target.value)} data-testid="cuadro-mes-select">
              {[...data.meses].reverse().map((m) => <option key={m.mes} value={m.mes}>{m.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {!comp ? (
        <div className="card p-8 max-w-lg" data-testid="cuadro-sin-datos">
          <p className="text-slate-600 mb-4">Aún no hay meses de ventas subidos.</p>
          <Link to="/subidas/ventas" className="btn btn-primary inline-flex items-center gap-2" data-testid="cuadro-subir-btn"><Upload size={14} /> Subir ventas</Link>
        </div>
      ) : (
        <>
          <ParcialAviso comp={comp} />
          <AccesosIndicadores />
          <Destacados items={DESTACADOS.filter((k) => filas[k]).map((k) => filas[k])} to="/ventas/cuadro#indicadores-ventas"
                      titulo="Indicadores clave de ventas" testid="destacados-ventas" />
          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">Rentabilidad</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MargenCard importId={actual?.import_id} margen={filas.margen?.actual ?? actual?.margen} />
              {filas.margen_pct && <KpiTile f={filas.margen_pct} comp={comp} />}
              {filas.stock_parado_vendido && <KpiTile f={filas.stock_parado_vendido} comp={comp} />}
              {filas.ratio_d_muerto && <KpiTile f={filas.ratio_d_muerto} comp={comp} />}
            </div>
          </section>
          <Evolucion meses={data.meses} mes={comp.mes} />
          <IndicadoresVentasSeccion data={data} />
          <SubirArriba />
        </>
      )}
    </div>
  );
}
